import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Configuration, Evaluation, Group, Item, ItemImage } from './configuration';
import { QuoteService } from '../quote.service';

@Component({
	selector: 'app-configurator',
	standalone: false,
	templateUrl: './configurator.component.html',
	styleUrl: './configurator.component.css'
})
export class ConfiguratorComponent implements OnInit, OnDestroy, AfterViewInit {

	configuration: Configuration | null = null;
	subscription?: Subscription;

    @ViewChildren('optionsContainer') container: ElementRef | null = null;
    
	constructor(private http: HttpClient,
		private activatedRoute: ActivatedRoute,
        private router: Router,
        private quoteService: QuoteService ) { }

	ngOnInit() {

		this.subscription = this.activatedRoute.params.subscribe(params => {
			if (params['sku']) {

                const sku = decodeURIComponent( params['sku'] );
                
				this.http.post<Configuration>('api/configurations/configure', sku )
					.subscribe(config => {
						this.configuration = config;
                        
                        if ( this.configuration && this.configuration.itemImages ) {
                            this.configuration.itemImages.forEach( i => {
                               i.selected = this.configuration?.itemImageId === i.id;
                            });
                            
                            // in firefox, the scroll can persist across different 
                            // configurations after leaving and re-entering the screen.
                            // so just reset it to the top every time.
                            let itvl = setInterval( () => {
                                if ( this.container && this.container.nativeElement ) {
                                    this.container.nativeElement.scrollTop = 0;
                                    clearInterval( itvl );
                                } else {
                                    const el = document.getElementById('options-container');
                                    
                                    if ( el ) {
                                        el.scrollTop = 0;
                                        clearInterval( itvl );
                                    }
                                }
                            }, 50);
                        }
                        
					});
			}
		});
	}
    
    ngAfterViewInit(): void {
        
        
    }

	ngOnDestroy() {
		this.subscription?.unsubscribe();
	}
    
    changeOption( group: Group, item: Item ) {
        
        if ( this.configuration ) {
            let request = `api/configurations/${this.configuration.id}/${group.id}/${item.id}/`       
                    
            if ( item.included ) {
                request += 'select';
            } else {
                request += 'deselect';
            }
            
            this.http.post<Evaluation>( request, null )
                .subscribe( evaluation => {
                    
                    if ( this.configuration == null) {
                        this.configuration = evaluation.configuration;
                    }
                    
                    // update each items state instead of replacing the entire
                    // configuration object.
                    // replacing the object results in repainting the dom, when,
                    // if scrolled down, causes the scroll to jump up
                    for (let g of this.configuration.groups) {
                        
                        this.updateOptions( g.options, evaluation.configuration );
                        this.updateOptions( g.additionalOptions, evaluation.configuration );
                        
                        for (let qg of g.quantityGroups) {
                         
                            this.updateOptions( qg.items, evaluation.configuration );
                        }
                        
                        let group = evaluation.configuration.groups
                            .filter( gr => gr.id === g.id )[0];
                        
                        g.required = group.required;
                    }
                });
        }
    }
    
    private updateOptions( items: Item[], configuration: Configuration ) {
        
        for ( let op of items ) {
                                    
            const updated = this.getUpdatedOption( op.sku, configuration );
            
            if ( updated ) {
                for (let key in op) {
                    op[ key ] = updated[ key ];
                }
            }
        }
    }
    
    
    private getUpdatedOption( sku: string, newConfiguration: Configuration ): Item|null {
        
        for ( let g of newConfiguration.groups ) {
                                
            for ( let op of g.options ) {
                
                if ( op.sku === sku ) {
                    return op;
                }
                
            }
            
            for (let op of g.additionalOptions) {
                                        
                if ( op.sku === sku ) {
                    return op;
                }
                
            }
            
            for ( let qg of g.quantityGroups ) {
             
                for ( let op of qg.items ) {
                    if ( op.sku === sku ) {
                        return op;
                    }
                }   
            }
        }
        
        return null;
        
    }
    
    changeQuantity( group: Group, item: Item ) {
            
        if ( this.configuration ) {
            let request = `api/configurations/${this.configuration.id}/${group.id}/${item.id}/`       
                    
            request += `quantity?q=${item.quantity}`;
            
            this.http.post<Evaluation>( request, null )
                .subscribe( evaluation => {
                    this.configuration = evaluation.configuration;
                });
        }
    }

	loaded(): boolean {
		return this.configuration != null;
	}
    
    toggleIncludeConfigurationImage(): void {
            
        if ( this.configuration ) {
                
            this.http.post( `api/configurations/${this.configuration.id}/include-configuration-image`,
                 this.configuration.includeConfigurationImage ).subscribe();
        }
    }
    
    toggleIncludePageNumbers(): void {
                
        if ( this.configuration ) {
                
            this.http.post( `api/configurations/${this.configuration.id}/include-part-numbers`,
                 this.configuration.includePartNumbers ).subscribe();
        }
    }
    
    selectImage( img: ItemImage ): void {
        
        if ( this.configuration ) {
            
            this.configuration.itemImages.forEach( i => i.selected = img === i );
                    
            this.http.post( `api/configurations/${this.configuration.id}/select-image`, img.id ).subscribe();
        }
    }

    showSave(): boolean {
        if ( this.configuration ) {
            
            return this.quoteService.configs.filter( c => 
                c.id === this.configuration?.id ).length === 0;
        }
        return false;
    }
    
    isValid(): boolean {
        
        let valid = false;
        
        if ( this.configuration ) {
        
            valid = true;
            
            for (let i = 0; i < this.configuration.groups.length && valid; i++) {
                
                valid = !this.configuration.groups[i].required;
            }
        }
        
        return valid;
    }
        
    save(): void {
        
        if ( this.configuration ) {
    
            const config = this.configuration;
            
            this.http.post<any>(`api/configurations/save`, this.configuration.id ).subscribe( response => {
                this.quoteService.addConfiguration( config );
                            
                this.router.navigate( ['/'] );
            });
        }
    }
    
    back(): void {
        this.router.navigateByUrl( '' );
    }

}
